import { Controller } from "@hotwired/stimulus"
import { chatConsumer } from "../chat/consumer"

const FIVE_MINUTES = 5 * 60 * 1000

// A room on screen: keeps the scroll at the bottom, groups messages by day
// and author, loads older pages, tells the server the room was read and
// registers presence (so push notifications stay quiet while you look).
export default class extends Controller {
  static targets = ["scroller", "list", "older", "empty"]
  static values = { id: Number, readUrl: String, olderUrl: String, highlight: Number, admin: Boolean, pageSize: Number }

  connect() {
    this.me = Number(document.body.dataset.userId)
    this.soundOn = this.readSoundPref()
    this.onSoundPref = (event) => { this.soundOn = event.detail.on }
    window.addEventListener("chat:sound-pref", this.onSoundPref)
    this.decorate()
    this.scrollToStart()
    this.observer = new MutationObserver((mutations) => this.changed(mutations))
    this.observer.observe(this.listTarget, { childList: true })
    this.onVisible = () => { if (document.visibilityState === "visible") this.markRead() }
    document.addEventListener("visibilitychange", this.onVisible)
    this.subscription = chatConsumer().subscriptions.create({ channel: "Chat::PresenceChannel", room_id: this.idValue })
    this.markRead()
  }

  disconnect() {
    this.observer?.disconnect()
    document.removeEventListener("visibilitychange", this.onVisible)
    window.removeEventListener("chat:sound-pref", this.onSoundPref)
    this.subscription?.unsubscribe()
  }

  // --- live changes ---------------------------------------------------

  // Only messages count: decorate() itself adds and removes day separators.
  changed(mutations) {
    const nodes = mutations.flatMap((m) => Array.from(m.addedNodes))
    const messages = nodes.filter((n) => n.classList?.contains("chat-msg"))
    if (messages.length === 0) return
    // A ping for anything a teammate just said (not my own echo).
    if (messages.some((n) => Number(n.dataset.creatorId) !== this.me)) this.ping()
    const wasNearBottom = this.nearBottom
    this.decorate()
    if (this.hasEmptyTarget) this.emptyTarget.hidden = this.messages.length > 0
    if (wasNearBottom) this.scrollToBottom()
    if (document.visibilityState === "visible") this.markRead()
  }

  get messages() {
    return Array.from(this.listTarget.querySelectorAll(":scope > .chat-msg"))
  }

  get nearBottom() {
    const el = this.scrollerTarget
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120
  }

  scrollToBottom() {
    this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
  }

  scrollToStart() {
    if (this.highlightValue) {
      const target = this.listTarget.querySelector(`[data-message-id="${this.highlightValue}"]`)
      if (target) {
        target.classList.add("chat-msg--highlight")
        target.scrollIntoView({ block: "center" })
        return
      }
    }
    this.scrollToBottom()
  }

  // Day separators and "continued" messages (same author within 5 minutes).
  decorate() {
    this.listTarget.querySelectorAll(":scope > .chat-day").forEach((el) => el.remove())
    let previous = null
    let previousDay = null
    for (const message of this.messages) {
      const at = new Date(message.dataset.at)
      const day = at.toDateString()
      if (day !== previousDay) {
        message.before(this.daySeparator(at))
        previousDay = day
        previous = null
      }
      const continued = previous &&
        previous.dataset.creatorId === message.dataset.creatorId &&
        at - new Date(previous.dataset.at) < FIVE_MINUTES
      message.classList.toggle("chat-msg--continued", Boolean(continued))
      previous = message
    }
  }

  daySeparator(date) {
    const el = document.createElement("div")
    el.className = "chat-day"
    const label = document.createElement("span")
    label.textContent = date.toLocaleDateString(document.documentElement.lang || undefined, { weekday: "long", day: "numeric", month: "long" })
    el.append(label)
    return el
  }

  // --- older pages ---------------------------------------------------

  async loadOlder() {
    const first = this.messages[0]
    if (!first || this.loading) return
    this.loading = true
    this.olderTarget.disabled = true
    try {
      const response = await fetch(`${this.olderUrlValue}?before=${first.dataset.messageId}`, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      const html = await response.text()
      const before = this.scrollerTarget.scrollHeight
      const template = document.createElement("template")
      template.innerHTML = html
      const count = template.content.querySelectorAll(".chat-msg").length
      this.listTarget.prepend(template.content)
      this.decorate()
      this.scrollerTarget.scrollTop += this.scrollerTarget.scrollHeight - before
      if (count < this.pageSizeValue) this.olderTarget.hidden = true
    } finally {
      this.loading = false
      this.olderTarget.disabled = false
    }
  }

  scrolled() {
    if (this.scrollerTarget.scrollTop < 40 && !this.olderTarget.hidden) this.loadOlder()
  }

  // --- sound ---------------------------------------------------------

  readSoundPref() {
    try { return localStorage.getItem("chat_sound") !== "off" } catch (_) { return true }
  }

  // A short two-tone chime, synthesised so no audio file ships. Browsers
  // block audio until the first interaction; a chat is full of clicks, so
  // by the time a message arrives the context resumes fine.
  ping() {
    if (!this.soundOn) return
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return
      this.audio ??= new Ctx()
      if (this.audio.state === "suspended") this.audio.resume()
      const now = this.audio.currentTime
      const gain = this.audio.createGain()
      gain.connect(this.audio.destination)
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35)
      ;[ 660, 880 ].forEach((freq, i) => {
        const osc = this.audio.createOscillator()
        osc.type = "sine"
        osc.frequency.value = freq
        osc.connect(gain)
        osc.start(now + i * 0.08)
        osc.stop(now + 0.35)
      })
    } catch (_) { /* audio not available */ }
  }

  // --- read state ----------------------------------------------------

  markRead() {
    clearTimeout(this.readTimer)
    this.readTimer = setTimeout(() => {
      fetch(this.readUrlValue, { method: "POST", headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" } })
    }, 400)
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
